import { useState } from "react";
import { X, Upload, Loader2, CheckCircle2, FileSpreadsheet } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { opts } from "./constants";

// CEN 2.0 FASE 2 sub-entrega 2 — Importador de la hoja "Cómputo" del Excel
// DESGLOSE DE TAREAS Y CARGA PORCENTUAL POR TIPO DE ÍTEMS. Pobla `tarea_templates`
// (catálogo global, reutilizable en cualquier presupuesto) — nunca toca el
// presupuesto abierto directamente.
export function ImportTareasDesgloseModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!open) return null;
  const reset = () => { setFile(null); setPreview(null); };
  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/catalogo-tareas/import-desglose/preview`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar el archivo");
    } finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!file) return;
    setConfirming(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/catalogo-tareas/import-desglose/confirm`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Plantillas: ${data.created} nuevas, ${data.updated} actualizadas`);
      reset();
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al confirmar la importación");
    } finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="import-tareas-desglose-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-violet-600" /> Importar Desglose de Tareas
          </h3>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="import-tareas-close-btn"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500">
            Subí el Excel de "DESGLOSE DE TAREAS Y CARGA PORCENTUAL" (hoja "Cómputo": N°, Descripción,
            Cantidad, Unidad + hasta 5 pares Tarea/%). Crea/actualiza plantillas reutilizables por
            descripción de ítem — no modifica el presupuesto abierto hasta que uses "Asignar Tareas".
          </p>
          {!preview && (
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" id="tareas-desglose-file" data-testid="import-tareas-file-input" />
              <label htmlFor="tareas-desglose-file" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer">
                <Upload size={14} /> {file ? file.name : "Elegir archivo Excel"}
              </label>
              {file && (
                <div className="mt-4">
                  <button onClick={handlePreview} disabled={loading}
                    className="inline-flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                    data-testid="import-tareas-preview-btn">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                    {loading ? "Analizando..." : "Analizar archivo (vista previa)"}
                  </button>
                </div>
              )}
            </div>
          )}
          {preview && (
            <div className="space-y-3" data-testid="import-tareas-preview">
              <div className="grid grid-cols-5 gap-2">
                <StatBadge label="Ítems" value={preview.n_items} color="bg-blue-50 text-blue-700" />
                <StatBadge label="Con desglose" value={preview.n_con_desglose} color="bg-emerald-50 text-emerald-700" />
                <StatBadge label="Plantillas nuevas" value={preview.n_nuevas_plantillas} color="bg-amber-50 text-amber-700" />
                <StatBadge label="A actualizar" value={preview.n_actualizar_plantillas} color="bg-violet-50 text-violet-700" />
                <StatBadge label="Inválidas (% ≠ 100%)" value={preview.n_invalidos} color={preview.n_invalidos > 0 ? "bg-red-50 text-red-700" : "bg-gray-50 text-gray-400"} />
              </div>
              {preview.n_invalidos > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
                  {preview.n_invalidos} fila(s) con % que no suman 100% — se omiten al confirmar.
                </div>
              )}
              <div className="border border-gray-200 rounded-xl overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-gray-500">N°</th>
                      <th className="text-left px-3 py-2 font-bold text-gray-500">Descripción</th>
                      <th className="text-center px-3 py-2 font-bold text-gray-500">Tareas</th>
                      <th className="text-center px-3 py-2 font-bold text-gray-500">Válido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.slice(0, 200).map((it, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="px-3 py-1.5 text-gray-400">{it.numero}</td>
                        <td className="px-3 py-1.5">{it.descripcion}</td>
                        <td className="px-3 py-1.5 text-center">{it.tareas.length}</td>
                        <td className="px-3 py-1.5 text-center">{it.valid ? "✓" : "✗"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {preview && (
          <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white pt-2">
            <button onClick={reset} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
              Elegir otro archivo
            </button>
            <button onClick={handleConfirm} disabled={confirming}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
              data-testid="import-tareas-confirm-btn">
              {confirming ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {confirming ? "Importando..." : "Confirmar importación"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div className={`rounded-lg px-3 py-2 text-center ${color}`}>
      <div className="text-lg font-bold tabular-nums">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}
